import React, { useRef, useEffect, useState, useCallback } from 'react';
import Hls from 'hls.js';
import { protectedAPI } from '../services/api';

const HlsLlPlayer = ({ name, uuid, channel = 0, barrier }) => {
  const videoRef = useRef(null);
  const [hls, setHls] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});
  const [isLive, setIsLive] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const HLS_LL_URL = `https://bnvm.ru:8443/stream/${uuid}/channel/0/hlsll/live/index.m3u8`;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = 0;

    if (Hls.isSupported()) {

      if (hls) {
        hls.destroy();
      }

      const hlsInstance = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 1, // Was 90
        maxBufferLength: 2, // Was 30
        maxMaxBufferLength: 3, // Was 60
        maxBufferSize: 3 * 1000 * 1000, // 3MB (Was 60MB)
        maxBufferHole: 0.5,
        highBufferWatchdogPeriod: 1, // Was 2
        nudgeOffset: 0.1,
        nudgeMaxRetry: 0, // Was 3
        maxFragLookUpTolerance: 0.1, // Was 0.2
        liveSyncDurationCount: 1, // Was 2
        liveMaxLatencyDurationCount: 2, //Was 5
        liveDurationInfinity: false,
        liveBackBufferLength: 0, // Was 10
        maxLiveSyncPlaybackRate: 1.1,
        
        enableSoftwareAES: true,
        strictLevel: true,
        initialLiveManifestSize: 1,
        manifestLoadingTimeOut: 10000,
        manifestLoadingMaxRetry: 3,
        manifestLoadingRetryDelay: 500,
        levelLoadingTimeOut: 10000,
        levelLoadingMaxRetry: 3,
        levelLoadingRetryDelay: 500,
        fragLoadingTimeOut: 20000,
        fragLoadingMaxRetry: 3,
        fragLoadingRetryDelay: 500,
      });

      hlsInstance.loadSource(HLS_LL_URL);
      hlsInstance.attachMedia(video);

      hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('Manifest parsed, attempting to play...');
        setIsLive(true);
        video.currentTime = video.duration;
        video.play().catch(e => {
          console.error('Auto-play failed:', e);
          setError('Ошибка.');
        });
      });

      hlsInstance.on(Hls.Events.LEVEL_LOADED, (event, data) => {
        setStats(prev => ({
          ...prev,
          level: data.level,
          bitrate: data.details?.totalduration ? data.details.totalduration : 'unknown'
        }));
      });

      hlsInstance.on(Hls.Events.FRAG_LOADED, (event, data) => {
        setStats(prev => ({
          ...prev,
          lastFragment: new Date().toLocaleTimeString(),
          frag: data.frag?.url
        }));
      });

      hlsInstance.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS Error:', data);
        
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.log('Network error, trying to recover...');
              hlsInstance.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log('Media error, recovering...');
              hlsInstance.recoverMediaError();
              break;
            default:
              setError(`Ошибка: ${data.details}`);
              hlsInstance.destroy();
              break;
          }
        }
      });

      const checkLivePosition = () => {
        if (video.duration && video.currentTime < video.duration - 3) {
          
          console.log('Duration:', video.duration);
          video.currentTime = video.duration - 1;
        }
      };

      const interval = setInterval(checkLivePosition, 1000);
      
      setHls(hlsInstance);

      return () => {
        clearInterval(interval);
        if (hlsInstance) {
          hlsInstance.destroy();
        }
      };
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = HLS_LL_URL;
      video.addEventListener('loadedmetadata', () => {
        video.currentTime = video.duration;
        setIsLive(true);
        video.play().catch(e => {
          console.error('Safari auto-play failed:', e);
          setError('Ошибка.');
        });
      });
    } else {
      setError('Ошибка');
    }
  }, []);

  const handlePlay = () => {
    const video = videoRef.current;
    if (video) {
      video.play().then(() => {
        setIsPlaying(true);
        setError(null);
      }).catch(e => {
        setError(`Ошибка: ${e.message}`);
      });
    }
  };

  const fetchOpenGate = useCallback(async () => {
    try {
      const response = await protectedAPI.updateBarrier(barrier, {
        status: true,
      });
      await new Promise(resolve => setTimeout(resolve, 3000));
      const response2 = await protectedAPI.updateBarrier(barrier, {
        status: false,
      });
    } catch (error) {
      alert("ERROR: " + error);
    } finally {
      //setLoading(false);
    }
  }, []);

  const handleOpenGate = () => {
    fetchOpenGate();
  }

  return (
    <div style={{ 
      border: '1px solid #ddd',
      borderRadius: '8px'
    }}>
      
      {error && (
        <div style={{
          color: 'red',
          padding: '10px',
          margin: '10px 0',
          border: '1px solid red',
          borderRadius: '4px',
          backgroundColor: '#ffe6e6'
        }}>
          {error}
          <button 
            onClick={handlePlay}
            style={{
              marginLeft: '10px',
              padding: '5px 10px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Продолжить
          </button>
        </div>
      )}

      <div style={{ position: 'relative' }}>
        <video
          ref={videoRef}
          controls
          autoPlay
          muted
          playsInline
          style={{
            width: '100%',
            borderRadius: '4px',
            backgroundColor: '#000'
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />
        {isPlaying && <button style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          padding: '5px 10px',
          borderRadius: '4px',
          fontSize: '18px'
          }}
          onClick={handleOpenGate}
          title="Открыть шлагбаум"
          >
            Открыть
        </button>}

      </div>
    </div>
  );
};

export default HlsLlPlayer;